import React, { useState } from 'react';
import { 
  FaBuilding, FaUsers, FaChartLine, FaShieldAlt, 
  FaCheckCircle, FaChevronDown, FaHandHoldingUsd, 
  FaLightbulb, FaBriefcase, FaGraduationCap 
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
const CorporateFinancialWellness = () => {
  // State for Accordion FAQ toggling
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      q: "What is a Corporate Financial Wellness Program?",
      a: "A Corporate Financial Wellness Program is an employer-sponsored initiative designed to educate and empower employees on managing their personal finances. It covers budgeting, tax planning, debt management, investment strategies, insurance, and retirement planning, helping employees reduce financial stress and achieve long-term security."
    },
    {
      q: "How does financial stress impact employee productivity?",
      a: "Studies consistently show that financial stress is a leading cause of workplace distraction, absenteeism, and lost productivity. Employees worried about debt, inflation, or lack of savings experience higher burnout rates. A wellness program helps alleviate these anxieties, leading to a more focused, engaged, and loyal workforce."
    },
    {
      q: "What topics are covered in Lotoswealth's wellness workshops?",
      a: "Our customized workshops cover a broad spectrum of topics, including smart budgeting, maximizing tax savings under various regimes, understanding mutual funds and equity markets, emergency fund planning, optimal insurance coverage, and building a structured retirement roadmap."
    },
    {
      q: "How can employers implement this program for their workforce?",
      a: "Implementation is seamless. We partner with your HR and leadership teams to design a modular program that fits your corporate culture—ranging from live interactive webinars and one-on-one employee advisory sessions to digital resource toolkits and financial health assessments."
    }
  ];

  return (
    <main className="corporate-wellness-page">
      <Helmet>
  {/* Basic SEO */}
  <title>Corporate Financial Wellness Services in Hyderabad | Employee Financial Wellness | Lotoswealth</title>

  <meta
    name="description"
    content="Lotoswealth provides Corporate Financial Wellness Services in Hyderabad, helping organizations improve employee financial well-being through financial education, investment planning, retirement planning, tax planning, wealth management, insurance guidance, and personalized financial advisory."
  />

  <meta
    name="keywords"
    content="Corporate Financial Wellness Hyderabad, Corporate Financial Wellness Services Hyderabad, Employee Financial Wellness Hyderabad, Financial Wellness Programs Hyderabad, Employee Financial Planning Hyderabad, Corporate Financial Planning Hyderabad, Workplace Financial Wellness Hyderabad, Employee Investment Planning Hyderabad, Retirement Planning for Employees Hyderabad, Tax Planning Hyderabad, Wealth Management Hyderabad, Financial Advisor Hyderabad, Investment Advisor Hyderabad, Financial Education Hyderabad, Employee Benefits Financial Planning Hyderabad, Corporate Wealth Management Hyderabad, HR Financial Wellness Hyderabad, Employee Wealth Management Hyderabad, Financial Literacy Programs Hyderabad, Lotoswealth"
  />

  <meta name="robots" content="index, follow" />
  <meta name="author" content="Lotoswealth" />
  <meta name="language" content="English" />

  <link
    rel="canonical"
    href="https://www.lotoswealth.com/corporate-financial-wellness"
  />

  {/* Open Graph */}
  <meta property="og:type" content="website" />
  <meta property="og:site_name" content="Lotoswealth" />
  <meta
    property="og:url"
    content="https://www.lotoswealth.com/corporate-financial-wellness"
  />
  <meta
    property="og:title"
    content="Corporate Financial Wellness Services in Hyderabad | Lotoswealth"
  />
  <meta
    property="og:description"
    content="Empower your workforce with Corporate Financial Wellness Services in Hyderabad. Lotoswealth offers financial education, investment planning, retirement planning, tax planning, and personalized financial advisory for employees."
  />
  <meta
    property="og:image"
    content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
  />

  {/* Twitter */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta
    name="twitter:title"
    content="Corporate Financial Wellness Services in Hyderabad | Lotoswealth"
  />
  <meta
    name="twitter:description"
    content="Enhance employee financial well-being with Corporate Financial Wellness Services in Hyderabad. Financial education, investment planning, retirement planning, tax planning, and wealth management solutions."
  />
  <meta
    name="twitter:image"
    content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
  />
</Helmet>
      {/* =========================================
          1. HERO SECTION (With Ticker & Slow Zoom)
      ========================================= */}
      <div className="hero-wrapper position-relative" style={{ height: '100vh', minHeight: '800px', overflow: 'hidden' }}>
        
        <div className="position-absolute top-0 start-0 w-100 h-100 z-0 animate-slow-zoom">
          <img 
            src="/images/corporate-financial-wellness.jpg" 
            alt="Corporate Financial Wellness" 
            className="w-100 h-100 object-fit-cover"
          />
        </div>

        {/* Slanted Panels Overlay */}
        <div className="panels-container position-absolute top-0 start-0 w-100 h-100 z-1 d-flex">
          <div className="slanted-panel panel-1"></div>
          <div className="slanted-panel panel-2"></div>
          <div className="slanted-panel panel-3"></div>
          <div className="slanted-panel panel-4"></div>
        </div>

        {/* --- Floating Main Header Text (Perfectly Centered) --- */}
        <div className="container hero-main-text text-center position-absolute top-50 start-50 translate-middle z-3 w-100 px-4">
          <h1 className="fw-bold display-4 animate-slide-up delay-1 mb-3 text-white" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
            Corporate Financial Wellness
          </h1>
          <h2 className="animate-slide-up delay-2 fw-bold text-white mb-4" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Empower Your Workforce, Elevate Productivity, and Drive Engagement
          </h2>
          <p className="hero-description animate-slide-up delay-3 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Welcome to Lotoswealth. An organization's greatest asset is its people. However, financial stress is one of the leading contributors to employee burnout, distraction, and turnover. Our Corporate Financial Wellness programs bridge this gap by equipping your team with the financial literacy and expert guidance they need to thrive both professionally and personally.
          </p>
          <p className="hero-description mt-3 animate-slide-up delay-4 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            We partner with forward-thinking employers to deliver comprehensive financial education workshops, one-on-one advisory sessions, tax-saving seminars, and retirement planning roadmaps directly to your workforce. 
          </p>
          <p className="hero-description mt-3 animate-slide-up delay-5 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Transform employee financial anxiety into confidence. Build a loyal, focused, and financially secure workforce with Lotoswealth.
          </p>
        </div>

        {/* --- Scrolling Stock Ticker --- */}
        <div className="stock-ticker position-absolute bottom-0 w-100 z-3 bg-dark text-white border-top border-bottom border-secondary py-2" style={{ opacity: 0.9 }}>
          <div className="ticker-track">
            {/* First Set */}
            <div className="ticker-item text-white border-end border-secondary"><strong>APOLLOHOSP</strong> 8,894.00 <span className="text-danger">-63.00 (-0.70%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>ASIANPAINT</strong> 2,768.60 <span className="text-success">21.30 (0.78%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>AXISBANK</strong> 1,250.00 <span className="text-success">20.50 (1.67%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJ-AUTO</strong> 11,519.00 <span className="text-danger">-2.00 (-0.02%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJFINSV</strong> 2,085.60 <span className="text-success">56.50 (2.78%)</span></div>
            
            {/* Duplicated Set for Infinite Scrolling */}
            <div className="ticker-item text-white border-end border-secondary"><strong>APOLLOHOSP</strong> 8,894.00 <span className="text-danger">-63.00 (-0.70%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>ASIANPAINT</strong> 2,768.60 <span className="text-success">21.30 (0.78%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>AXISBANK</strong> 1,250.00 <span className="text-success">20.50 (1.67%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJ-AUTO</strong> 11,519.00 <span className="text-danger">-2.00 (-0.02%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJFINSV</strong> 2,085.60 <span className="text-success">56.50 (2.78%)</span></div>
          </div>
        </div>
      </div>

      {/* =========================================
          2. WHY CORPORATE WELLNESS MATTERS (Soft Teal Theme)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f0f9f6' }}>
        <div className="container py-4">
          <div className="row align-items-center g-5">
            <div className="col-lg-6 animate-slide-up delay-1">
              <h2 className="fw-bold mb-4 text-dark">
                The Business Case for Employee Financial Wellness
              </h2>
              <p className="text-muted line-height-lg">
                Financial stress is a silent productivity killer. When employees are overwhelmed by credit card debt, inadequate savings, or confusion around tax planning, their mental bandwidth at work suffers dramatically.
              </p>
              <p className="text-muted line-height-lg">
                By investing in a robust financial wellness program, employers actively demonstrate care for their teams' holistic well-being. This translates directly into higher employee retention, lower absenteeism, improved engagement, and enhanced workplace morale.
              </p>
            </div>
            <div className="col-lg-6 animate-slide-up delay-2">
              <div className="p-4 bg-white rounded-4 shadow-sm border-start border-teal border-5">
                <h4 className="fw-bold text-teal mb-3">Key Benefits for Organizations</h4>
                <ul className="list-unstyled text-muted mb-0">
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Reduced Stress:</strong> Lower financial anxiety leads to sharper workplace focus.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Higher Retention:</strong> Employees value holistic wellness benefits, reducing attrition.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Enhanced Morale:</strong> Demonstrates genuine organizational support for staff futures.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Smart Tax Optimization:</strong> Staff learn to structure salaries and investments efficiently.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          3. OUR WELLNESS SERVICES (Crisp White)
      ========================================= */}
      <section className="py-5 bg-white" style={{ backgroundImage: "url('/images/lotoswealth-bg.jpg')", backgroundSize: 'cover', backgroundPosition: 'center' }}>
        
        <div className="container py-4">
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-white">💼 Our Corporate Wellness Offerings</h2>
            <p className="text-white">Modular programs customized to match your company culture and employee demographics.</p>
          </div>

          <div className="row g-4">
            
            <div className="col-lg-4 col-md-6 animate-slide-up delay-1">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaGraduationCap size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Financial Literacy Workshops</h5>
                <p className="text-muted small">Engaging, jargon-free seminars on budgeting, emergency funds, debt management, and the fundamentals of long-term investing.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-2">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaHandHoldingUsd size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Tax-Saving & Salary Structuring</h5>
                <p className="text-muted small">Expert sessions helping employees optimize their CTC components, understand new vs. old tax regimes, and plan tax-saving investments legally.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-3">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaUsers size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">1-on-1 Employee Advisory</h5>
                <p className="text-muted small">Confidential, personalized financial counseling sessions where employees can discuss individual goals, mutual funds, and retirement plans.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-4">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaChartLine size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Retirement & EPF Guidance</h5>
                <p className="text-muted small">Educating employees on maximizing EPF/NPS benefits, compounding early, and structuring sustainable post-retirement roadmaps.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-5">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaShieldAlt size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Insurance & Risk Awareness</h5>
                <p className="text-muted small">Workshops on evaluating adequate life and health insurance coverage, protecting families against unexpected medical emergencies.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-6">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaLightbulb size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Digital Resource Hubs</h5>
                <p className="text-muted small">Providing employees with access to financial calculators, educational guides, and investment tracking tools via a seamless platform.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          4. OUR 5-STEP IMPLEMENTATION PROCESS (Dark Slate)
      ========================================= */}
      <section className="py-5 text-white" style={{ backgroundColor: '#0a192f' }}>
        <div className="container py-4">
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-white">🔄 How We Implement Wellness Programs</h2>
            <p className="text-light opacity-75">A streamlined, HR-friendly rollout process.</p>
          </div>

          <div className="row g-4">
            
            <div className="col-md-6 col-lg-4 animate-slide-up delay-1">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 1 – Needs Assessment</h5>
                <p className="small text-muted mb-0">We collaborate with your HR team to understand employee demographics, income brackets, and primary financial pain points.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4 animate-slide-up delay-2">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 2 – Program Customization</h5>
                <p className="small text-muted mb-0">Designing a tailored calendar of workshops, webinars, and counseling sessions that fit seamlessly into your corporate schedule.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4 animate-slide-up delay-3">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 3 – Launch & Engagement</h5>
                <p className="small text-muted mb-0">Executing engaging, interactive sessions led by expert wealth advisors with a strong focus on practical, actionable takeaways.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-6 animate-slide-up delay-4">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 4 – 1-on-1 Consultations</h5>
                <p className="small text-muted mb-0">Opening secure booking slots for employees seeking personalized guidance on their specific tax or investment portfolios.</p>
              </div>
            </div>

            <div className="col-md-12 col-lg-6 animate-slide-up delay-5">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 5 – Impact Feedback & Review</h5>
                <p className="small text-muted mb-0">Providing HR metrics on employee engagement and feedback scores to measure the program's success and refine future sessions.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          5. WHO WE SERVE & WHY CHOOSE US (Soft Blue-Gray)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#eef2f5' }}>
        <div className="container py-4">
          <div className="row g-5">
            
            {/* Target Audience */}
            <div className="col-lg-5 animate-slide-up delay-1">
              <h3 className="fw-bold mb-4 text-dark">👥 Who Benefits from Corporate Wellness?</h3>
              <p className="text-muted small mb-4">Our programs scale effortlessly across diverse organizations:</p>
              <div className="d-flex flex-wrap gap-2">
                {[
                  "IT & Tech Enterprises", "Startup Founders & HR Teams",
                  "Manufacturing & Corporate Offices", "Multinational Corporations (MNCs)",
                  "Fast-Growing SMBs", "Financial Institutions",
                  "Remote & Hybrid Teams", "Employee-Centric Organizations"
                ].map((item, idx) => (
                  <span key={idx} className="badge bg-white text-dark border p-2 shadow-sm fs-6">
                    {item}
                  </span>
                ))}
              </div>
            </div>

            {/* Why Choose Lotoswealth */}
            <div className="col-lg-7 animate-slide-up delay-2">
              <div className="p-4 bg-white rounded-4 shadow-sm h-100">
                <h3 className="fw-bold mb-3 text-dark">🏆 Why Partner with Lotoswealth?</h3>
                <p className="text-muted small mb-4">Delivering corporate wellness requires engaging speakers and professional expertise. Organizations trust us because we offer:</p>
                <div className="row g-3">
                  {[
                    "Engaging, Jargon-Free Workshops", "Experienced & Certified Financial Advisors",
                    "Customizable Modular Programs", "Strict 1-on-1 Employee Data Privacy",
                    "Actionable Investment & Tax Strategies", "Seamless HR Integration & Support",
                    "Measurable Engagement Metrics", "Holistic Employee Well-Being Focus"
                  ].map((item, idx) => (
                    <div key={idx} className="col-md-6 d-flex align-items-center">
                      <FaCheckCircle className="text-success me-2 flex-shrink-0" />
                      <span className="small text-muted fw-bold">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          6. FREQUENTLY ASKED QUESTIONS (FAQ)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container py-4" style={{ maxWidth: '850px' }}>
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-dark">❓ Frequently Asked Questions</h2>
            <p className="text-muted">Common queries regarding Corporate Financial Wellness Programs</p>
          </div>

          <div className="d-flex flex-column gap-3 animate-slide-up delay-2">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="bg-white rounded-3 shadow-sm border overflow-hidden"
              >
                <button 
                  className="w-100 text-start p-3 border-0 bg-white d-flex justify-content-between align-items-center fw-bold text-dark"
                  onClick={() => toggleFaq(index)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="pe-3">{faq.q}</span>
                  <FaChevronDown 
                    className={`text-teal transition-transform ${openFaq === index ? 'rotate-180' : ''}`} 
                    style={{ transition: 'transform 0.3s ease', minWidth: '16px' }}
                  />
                </button>
                {openFaq === index && (
                  <div className="p-3 pt-0 text-muted small border-top bg-light lh-lg">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          7. FINAL CTA SECTION (Deep Navy)
      ========================================= */}
      <section className="py-5 text-white text-center" style={{ backgroundColor: '#0f1e2d' }}>
        <div className="container py-4">
          <h2 className="fw-bold mb-3 animate-slide-up delay-1">Ready to Elevate Your Employees' Financial Well-Being?</h2>
          <p className="text-light opacity-75 mb-4 animate-slide-up delay-2 mx-auto" style={{ maxWidth: '600px' }}>
            Equip your workforce with the tools they need to reduce stress and build lasting financial security. Partner with Lotoswealth today.
          </p>
          <button className="btn-custom-teal hover-lift px-5 py-3 fs-5 border-0 rounded text-white shadow animate-slide-up delay-3">
            Schedule a Corporate Wellness Consultation
          </button>
        </div>
      </section>

    </main>
  );
};

export default CorporateFinancialWellness;